import React from "react"
import { graphql } from "gatsby"
import { isFilled } from "@prismicio/helpers"
import { PrismicRichTextProps } from "@prismicio/react"
import clsx from "clsx"

import { MapDataToPropsCtx } from "../components/MapSlicesToComponents"
import { BoundedBox } from "../components/BoundedBox"
import { Text } from "../components/Text"
import { PageDataBodyNewsletterSignupFragment } from "../graphql.gen"
import { Button } from "../components/Button"
import { HEADING_CLASSES, RichText } from "../components/RichText"
import { TextField } from "./PageDataBodyForm"

import * as cssStyles from "../components/RichText.module.css"

const componentOverrrides: PrismicRichTextProps["components"] = {
	heading2: (props) => (
		<Text asChild variant="heading3" className={HEADING_CLASSES}>
			<h2>{props.children}</h2>
		</Text>
	),

	paragraph: (props) => (
		<Text
			asChild
			variant="paragraph1"
			fontFamily="sans"
			className={cssStyles.paragraph}
			suppressHydrationWarning
		>
			<p>{props.children}</p>
		</Text>
	),
}

type State = "success" | "init" | "pending" | "error"
type Props = ReturnType<typeof mapDataToProps>

export const PageDataBodyNewsletterSignup = (props: Props) => {
	const [state, setState] = React.useState<State>("init")

	async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()

		setState("pending")

		const form = new FormData(e.currentTarget)
		const payload = {
			firstName: form.get("firstName"),
			lastName: form.get("lastName"),
			email: form.get("email"),
		}

		const res = await fetch("/api/newsletter", {
			method: "POST",
			body: JSON.stringify(payload),
			headers: {
				"content-type": "application/json",
			},
		})
		if (!res.ok) return setState("error")

		setState("success")
	}

	function resetForm() {
		setState("init")
	}

	return (
		<BoundedBox.Outer className="bg-gray-93">
			<BoundedBox.Inner maxWidth="form">
				{state !== "success" ? (
					<>
						<div className="text-center">
							<Text asChild variant="heading2">
								<h2>{props.heading}</h2>
							</Text>
							{state === "error" && (
								<Text asChild variant="paragraph1" className="text-red mt-8">
									<p>Something went wrong. Please try again.</p>
								</Text>
							)}
						</div>
						<form
							onSubmit={onSubmit}
							className={clsx(
								"grid gap-y-8 md:gap-y-9 gap-x-6 md:grid-cols-2 transition mt-12",
								state === "pending" && "opacity-25 pointer-events-none"
							)}
						>
							<TextField label="First Name" name="firstName" required />
							<TextField label="Last Name" name="lastName" required />
							<TextField label="Email" fullWidth name="email" required />
							<Button color="black" className="col-span-full">
								{state === "pending" ? "Subscribing..." : props.buttonText}
							</Button>
						</form>
					</>
				) : (
					<div>
						{isFilled.richText(props.confirmationMesssage) && (
							<RichText
								field={props.confirmationMesssage}
								componentOverrides={componentOverrrides}
							/>
						)}

						<Button
							color="black"
							onClick={resetForm}
							size="small"
							className="mt-8"
						>
							Reset Form
						</Button>
					</div>
				)}
			</BoundedBox.Inner>
		</BoundedBox.Outer>
	)
}

export function mapDataToProps({
	data,
}: MapDataToPropsCtx<PageDataBodyNewsletterSignupFragment>) {
	return {
		buttonText: data.primary.button_text || "Subscribe",
		heading: data.primary.heading?.text,
		confirmationMesssage: data.primary.confirmation_message?.richText,
	}
}

export const fragment = graphql`
	fragment PageDataBodyNewsletterSignup on PrismicPageDataBodyNewsletterSignup {
		primary {
			button_text
			heading {
				text
			}
			confirmation_message {
				richText
			}
		}
	}
`

export default PageDataBodyNewsletterSignup
